import { Component, EventEmitter, Inject, OnInit, Output, OnDestroy, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { User } from '@core/models/user.model';
import { Observable, Subject, fromEvent } from 'rxjs';
import { debounceTime, distinctUntilChanged, tap, takeUntil, throttleTime } from 'rxjs/operators';
import { Store, Select } from '@ngxs/store';
import { CollaboratorsState } from '@shared/store/collaborators/collaborators.state';
import { FetchCollaboratorPhotos } from '@shared/store/collaborators/collaborators.actions';
import { EntityMap } from '@shared/models/types';

export interface CollaboratorWithPermission {
  user: User;
  permission: 'view' | 'editor';
  selected: boolean;
}

@Component({
  selector: 'hmt-add-collaborators',
  templateUrl: './add-collaborators.component.html',
  styleUrls: ['./add-collaborators.component.scss'],
  standalone: false
})
export class AddCollaboratorsComponent implements OnInit, AfterViewInit, OnDestroy {

  @Output() searchCollaborator: EventEmitter<string> = new EventEmitter();
  @Output() loadMoreCollaborators: EventEmitter<number> = new EventEmitter();

  @ViewChild('collaboratorsListContainer', { static: false }) collaboratorsListContainer!: ElementRef<HTMLDivElement>;

  @Select(CollaboratorsState.getCollaboratorPhotos) collaboratorPhotos$: Observable<EntityMap<string, string>>;

  searchControl = new FormControl('');
  selectedTab: 'internal' | 'guest' = 'internal';
  collaboratorsList: CollaboratorWithPermission[] = [];
  selectAll = false;
  private selectedCollaboratorIds = new Set<string>();
  private initiallyPreSelectedIds = new Set<string>();
  private allUsersMap = new Map<string, User>();
  collaboratorPhotos: EntityMap<string, string> = {};
  private destroy$ = new Subject<void>();
  private currentPage = 0;
  private hasMoreItems = true;
  private isLoadingMore = false;
  private currentSearchText = '';

  constructor(
    private dialogRef: MatDialogRef<AddCollaboratorsComponent>,
    private store: Store,
    @Inject(MAT_DIALOG_DATA) public data: {
      internalCollaborators: Observable<User[]>,
      preSelectedCollaborators?: User[]
    }
  ) { }

  ngOnInit(): void {
    // Initialize initially pre-selected collaborators
    if (this.data.preSelectedCollaborators) {
      this.data.preSelectedCollaborators.forEach(user => {
        this.initiallyPreSelectedIds.add(user.id);
        this.selectedCollaboratorIds.add(user.id);
        this.allUsersMap.set(user.id, user);
      });
    }

    // Initialize search
    this.searchControl.valueChanges
      .pipe(
        distinctUntilChanged(),
        debounceTime(500),
        tap((searchText: string) => {
          // Reset pagination when search changes
          if (this.currentSearchText !== searchText) {
            this.currentSearchText = searchText || '';
            this.currentPage = 0;
            this.hasMoreItems = true;
            this.collaboratorsList = [];
            this.allUsersMap.clear();
          }
          this.searchCollaborator.emit(searchText);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();

    // Subscribe to collaborators list
    this.data.internalCollaborators
      .pipe(takeUntil(this.destroy$))
      .subscribe(users => {
        if (!users || users.length === 0) {
          this.hasMoreItems = false;
          return;
        }

        // Check if this is a new search (page 0) or pagination
        if (this.currentPage === 0) {
          // Reset list for new search or initial load
          this.collaboratorsList = [];
          this.allUsersMap.clear();
        }

        // Update the map with all users we've seen
        users.forEach(user => {
          this.allUsersMap.set(user.id, user);
        });

        // Append new collaborators (avoid duplicates)
        const existingIds = new Set(this.collaboratorsList.map(c => c.user.id));
        const newCollaborators: CollaboratorWithPermission[] = users
          .filter(user => !existingIds.has(user.id))
          .map(user => ({
            user,
            permission: 'view' as 'view' | 'editor',
            selected: this.selectedCollaboratorIds.has(user.id)
          }));

        this.collaboratorsList = [...this.collaboratorsList, ...newCollaborators];
        this.updateSelectAllState();

        // Update pagination state
        this.hasMoreItems = users.length >= 20; // Assuming pageSize is 20
        this.isLoadingMore = false;

        // Fetch photos for users who have photo IDs
        const photoIds = users
          .map(u => u.photo)
          .filter(photoId => photoId && photoId.trim() !== '');
        
        if (photoIds.length > 0) {
          this.store.dispatch(new FetchCollaboratorPhotos(photoIds));
        }
      });

    // Subscribe to collaborator photos
    this.collaboratorPhotos$
      .pipe(takeUntil(this.destroy$))
      .subscribe(photos => {
        this.collaboratorPhotos = photos || {};
      });
  }

  ngAfterViewInit(): void {
    // Setup scroll listener after view is initialized
    setTimeout(() => {
      this.setupScrollListener();
    }, 0);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupScrollListener(): void {
    if (!this.collaboratorsListContainer) {
      return;
    }

    const container = this.collaboratorsListContainer.nativeElement;
    
    fromEvent(container, 'scroll')
      .pipe(
        throttleTime(100),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        const scrollTop = container.scrollTop;
        const scrollHeight = container.scrollHeight;
        const clientHeight = container.clientHeight;
        
        // Load more when user scrolls to within 100px of the bottom
        const threshold = 100;
        const isNearBottom = scrollTop + clientHeight >= scrollHeight - threshold;

        if (isNearBottom && this.hasMoreItems && !this.isLoadingMore) {
          this.loadMore();
        }
      });
  }

  private loadMore(): void {
    if (this.isLoadingMore || !this.hasMoreItems) {
      return;
    }

    this.isLoadingMore = true;
    this.currentPage++;
    this.loadMoreCollaborators.emit(this.currentPage);
  }

  getPhotoUrl(photoId: string): string | null {
    return this.collaboratorPhotos[photoId] || null;
  }

  isPreSelected(user: User): boolean {
    if (!this.data.preSelectedCollaborators) return false;
    return this.data.preSelectedCollaborators.some(c => c.id === user.id);
  }

  isSelected(userId: string): boolean {
    return this.selectedCollaboratorIds.has(userId);
  }

  toggleSelectAll(): void {
    this.selectAll = !this.selectAll;
    this.collaboratorsList.forEach(c => {
      c.selected = this.selectAll;
      if (this.selectAll) {
        this.selectedCollaboratorIds.add(c.user.id);
      } else {
        this.selectedCollaboratorIds.delete(c.user.id);
      }
    });
  }

  toggleCollaborator(collaborator: CollaboratorWithPermission): void {
    collaborator.selected = !collaborator.selected;
    if (collaborator.selected) {
      this.selectedCollaboratorIds.add(collaborator.user.id);
    } else {
      this.selectedCollaboratorIds.delete(collaborator.user.id);
    }
    this.updateSelectAllState();
  }

  updateSelectAllState(): void {
    this.selectAll = this.collaboratorsList.length > 0 &&
                     this.collaboratorsList.every(c => this.selectedCollaboratorIds.has(c.user.id));
  }

  changePermission(collaborator: CollaboratorWithPermission, permission: 'view' | 'editor'): void {
    collaborator.permission = permission;
  }

  getSelectedCollaborators(): CollaboratorWithPermission[] {
    // Return all selected collaborators, not just currently visible ones
    return Array.from(this.selectedCollaboratorIds).map(userId => {
      const user = this.allUsersMap.get(userId);
      if (!user) return null;

      // Find the current permission if this user is visible, otherwise default to 'view'
      const visibleCollaborator = this.collaboratorsList.find(c => c.user.id === userId);
      const permission = visibleCollaborator ? visibleCollaborator.permission : 'view';

      return {
        user,
        permission,
        selected: true
      };
    }).filter(c => c !== null);
  }

  get selectedCount(): number {
    return this.getSelectedCollaborators().length;
  }

  get selectedNames(): string {
    const selected = this.getSelectedCollaborators();
    if (selected.length === 0) return '';
    if (selected.length === 1) return `${selected[0].user.firstName} ${selected[0].user.lastName}`;
    if (selected.length === 2) return `${selected[0].user.firstName} ${selected[0].user.lastName}, ${selected[1].user.firstName} ${selected[1].user.lastName}`;
    return `${selected[0].user.firstName} ${selected[0].user.lastName}, ${selected[1].user.firstName} ${selected[1].user.lastName}`;
  }

  get remainingCount(): number {
    const count = this.selectedCount;
    return count > 2 ? count - 2 : 0;
  }

  invite(): void {
    const selectedCollaborators = this.getSelectedCollaborators();
    this.dialogRef.close(selectedCollaborators);
  }

  close(): void {
    this.dialogRef.close();
  }

  switchTab(tab: 'internal' | 'guest'): void {
    this.selectedTab = tab;
  }

  trackByUserId(index: number, collab: CollaboratorWithPermission): string {
    return collab.user.id || index.toString();
  }
}

